import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Typography,
  Option,
} from '@beeline/design-system-react/extracted-styles';

import { EmployeePermission } from 'src/api/permissions';
import { getEmployeeSpaces } from 'src/api/spaces';
import { BookingObjectAttribute } from 'src/components/BookingObjectAttribute/BookingObjectAttribute';
import { EmployeeInfo } from 'src/components/EmployeeInfo/EmployeeInfo';
import { FormDatesPicker } from 'src/components/FormDatesPicker/FormDatesPicker';
import { FormPersonAutocomplete } from 'src/components/FormPersonAutocomplete/FormPersonAutocomplete';
import { Section } from 'src/components/Section/Section';
import { SpaceInfoComponent } from 'src/components/SpaceInfo/SpaceInfo';
import {
  getDeskLocation,
  getDesksState,
  getDesksTimeBounds,
} from 'src/store/desks/selectors';
import { messagesActions } from 'src/store/messages/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserState } from 'src/store/user/selectors';
import { Desk } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { formatDate } from 'src/utils/formatDate';
import { getDateTime } from 'src/utils/getDateTime';
import { toAddress } from 'src/utils/toAddress';

export interface DeskBookingSidesheetFieldsProps {
  desk?: Desk;
  permissions: Set<EmployeePermission>;
  bookable: boolean;
}

export const DeskBookingSidesheetFields: FC<
  DeskBookingSidesheetFieldsProps
> = ({ desk, permissions, bookable }) => {
  const dispatch = useAppDispatch();

  const { data } = useSelector(getUserState);
  const { town, building, floor } = useSelector(getDeskLocation);
  const { min: minDate, max: maxDate } = useSelector(getDesksTimeBounds);
  const { excludedDates } = useSelector(getDesksState);

  const bookingData = toAddress({
    town: town?.townName,
    building: building?.address,
    floor: floor?.title,
  });
  const bookingDate = useMemo((): string | undefined => {
    const result = desk?.status?.bookedFrom
      ? getDateTime({
          date: new Date(desk.status.bookedFrom),
          timezoneOffset: town?.timeZone,
        })
      : undefined;
    return result ? formatDate(result) : undefined;
  }, [desk?.status?.bookedFrom, town?.timeZone]);

  const validateBookedFor = useCallback(
    async (value: Option<string> | null) => {
      if (!desk?.spaceId) return '';

      if (!value || value.id === data?.employeeNumber) {
        return data?.spaces.has(desk.spaceId)
          ? ''
          : 'Вы не входите в пространство';
      }

      try {
        const employeeSpaces = new Set(
          await getEmployeeSpaces(value.id as number),
        );
        return employeeSpaces.has(desk.spaceId)
          ? ''
          : 'Сотрудник не входит в пространство';
      } catch (e) {
        dispatch(messagesActions.show({ message: defaultServerError }));
        return '';
      }
    },
    [data?.employeeNumber, data?.spaces, desk?.spaceId, dispatch],
  );

  return (
    <>
      <Section title='Данные рабочего места' testId='deskDataSection'>
        <Typography variant='body3' data-testid='deskOffice'>
          {bookingData}
        </Typography>
        {desk?.byWindow && (
          <BookingObjectAttribute testId='deskByWindow'>
            Рядом с окном
          </BookingObjectAttribute>
        )}
        {desk?.withDivider && (
          <BookingObjectAttribute testId='deskWithCharger'>
            Акустическая перегородка
          </BookingObjectAttribute>
        )}
        {desk?.withCharger && (
          <BookingObjectAttribute testId='deskWithCharger'>
            Зарядное устройство
          </BookingObjectAttribute>
        )}
        {desk?.withMonitor && (
          <BookingObjectAttribute testId='deskWithMonitor'>
            Монитор
          </BookingObjectAttribute>
        )}
      </Section>

      {desk?.responsibleEmployeeNumber ? (
        <Section title='Закреплено за' testId='deskBookedBySection'>
          <EmployeeInfo
            employeeNumber={desk.responsibleEmployeeNumber}
            testId='deskBookedBy'
          />
        </Section>
      ) : (
        <>
          {desk?.status?.responsibleEmployeeNumber && (
            <Section
              title='На кого забронировано'
              testId='deskBookedForSection'
            >
              <EmployeeInfo
                employeeNumber={desk.status.responsibleEmployeeNumber}
                testId='deskBookedFor'
              />
            </Section>
          )}

          {desk?.status?.responsibleEmployeeNumber &&
            desk.status.authorEmployeeNumber &&
            desk.status.responsibleEmployeeNumber !==
              desk.status.authorEmployeeNumber && (
              <Section title='Кем забронировано' testId='deskBookedBySection'>
                <EmployeeInfo
                  employeeNumber={desk.status.authorEmployeeNumber}
                  testId='deskBookedBy'
                />
              </Section>
            )}

          {bookingDate && (
            <Section title='Дата бронирования' testId='deskDateSection'>
              <Typography variant='body3' data-testid='deskDate'>
                {bookingDate}
              </Typography>
            </Section>
          )}

          {bookable && (
            <Section title='Даты бронирования' testId='deskDateSection'>
              <FormDatesPicker
                name='dates'
                minDate={minDate ? new Date(minDate) : undefined}
                maxDate={maxDate ? new Date(maxDate) : undefined}
                excludedDates={excludedDates}
                minCount={1}
                key={[...excludedDates].join('-')}
                excludedDateErrorMessage='Рабочее место уже забронировано'
              />
            </Section>
          )}

          {desk?.spaceId && (
            <Section title='Закреплено за' testId='deskSpaceSection'>
              <SpaceInfoComponent spaceId={desk.spaceId} testId='deskSpace' />
            </Section>
          )}

          {!desk?.status &&
            permissions.has(EmployeePermission.BookDesks) &&
            permissions.has(EmployeePermission.BookForOthers) && (
              <Section
                title='Забронировать на коллегу'
                testId='deskBookForSection'
              >
                <FormPersonAutocomplete
                  name='bookedFor'
                  type='select'
                  dataTestId='deskBookFor'
                  validate={validateBookedFor}
                />
              </Section>
            )}
        </>
      )}
    </>
  );
};
